// Moved from `src/hooks/mutation/useMutateToggleTeamSettings.ts` (now the app
// delegate) with the OT4 mutation slice (ARCFE-1033). The densest of the four
// team-setting toggles: four severances (ARCFE-921).
//
// `userRoleIds` arrives as an EXPLICIT PARAMETER rather than being read from
// the package's own `useCombinedQueriesTeamGeneralInfo`, and that is a
// behaviour-preservation choice, not a style one: the APP composer supplies
// that projection's `onTeamDetailsFetchError` toast (see
// `src/hooks/query/useCombinedQueriesTeamGeneralInfo.ts`). Calling the package
// core here instead would silently drop the team-details error toast for
// fetches this observer triggers. Delivery section 3 permits explicit
// parameters for exactly this.
//
// The `selectedReviewMemberAtom` write is app UI state, so the package computes
// the CONDITION (data logic over `userRoleIds` and the request) and the
// delegate performs the write.
import { logger } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  GET_TEAM_DETAILS,
  GET_TEAM_MEMBERS_WITH_RELATIONSHIP,
  type UpdateTeamRequest,
} from '../../contracts';
import { updateTeamSettings } from '../api/teams';

export interface ToggleTeamSettingsVariables {
  teamId: string;
  updateTeamRequest: Partial<UpdateTeamRequest>;
}

export interface UseMutateToggleTeamSettingsOptions {
  /**
   * The current user's role ids in the team. Supplied by the app composer so
   * its team-details error toast keeps firing (see the header).
   */
  userRoleIds?: string[];
  /** App-reaction port: fired where `invalidateCurrentUser()` fired. */
  onCurrentUserRefreshNeeded?: () => void;
  /**
   * App-reaction port: fired where the success toast and the conditional
   * Rematch `trackToggleTeamMfa` dispatch fired, in that order.
   */
  onSettingsUpdated?: (variables: ToggleTeamSettingsVariables) => void;
  /**
   * App-reaction port: fired when the caller can no longer view others'
   * sessions, where `setSelectedUser(user?.kinde_user_id)` fired.
   */
  onSessionReviewSelectionReset?: () => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onSettingsUpdateError?: (error: Error) => void;
}

/** PATCH /teams/{team_id}/settings */
export function useMutateToggleTeamSettings(
  options?: UseMutateToggleTeamSettingsOptions
) {
  const queryClient = useQueryClient();
  const userRoleIds = options?.userRoleIds;

  return useMutation({
    mutationKey: ['toggleTeamSettings'],
    mutationFn: async (input: ToggleTeamSettingsVariables) => {
      await updateTeamSettings({
        teamId: input.teamId,
        updateTeamRequest: input.updateTeamRequest,
      });
    },
    onSuccess: async (_, input) => {
      await queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId: input.teamId }],
      });
      await queryClient.invalidateQueries({
        queryKey: [GET_TEAM_MEMBERS_WITH_RELATIONSHIP, { teamId: input.teamId }],
      });

      options?.onCurrentUserRefreshNeeded?.();

      options?.onSettingsUpdated?.(input);

      if ('session_configuration' in input.updateTeamRequest) {
        const userRoleIdInSessionReview = userRoleIds?.filter((roleId) =>
          input.updateTeamRequest.session_configuration?.role_ids_can_view_others_sessions?.includes(
            roleId
          )
        );

        if (
          !input.updateTeamRequest.session_configuration?.allow_view_others_sessions ||
          userRoleIds?.length === 0 ||
          userRoleIdInSessionReview?.length === 0
        ) {
          options?.onSessionReviewSelectionReset?.();
        }
      }
    },
    onError: (error) => {
      logger.error('Error:', error);
      options?.onSettingsUpdateError?.(error);
    },
  });
}
